/**
 * Questionnaire type detection helpers.
 *
 * telecom_self_v1 is a "self-assessment only" questionnaire —
 * no reviewer workflow, only respondent scores matter.
 */

const SELF_ASSESSMENT_QUESTIONNAIRES = new Set([
  "telecom_self_v1",
  "hrv_self_v1",
  "ai_llm_self_v1",
  // MSP Starter Assessments (all self-assessment)
  "cyber_posture_v1",
  "data_privacy_law_v1",
  "iam_compromise_v1",
  "ir_ransomware_v1",
  "vendor_risk_lite_v1",
  "cyber_insurance_v1",
  "telecom_pre_investment_triage_v1",
]);

/**
 * Questionnaires that enforce strict evidence validation rules:
 * 1. ALL questions must be answered before submission
 * 2. Score >= 50 (Developing+) requires at least ONE of: evidence upload, link, or non-empty comment
 *
 * This is the canonical "evidence-required" behavior set.
 * Originally created for telecom-style questionnaires, but reused by any
 * questionnaire that needs the same validation behavior.
 */
const EVIDENCE_REQUIRED_QUESTIONNAIRES = new Set([
  "telecom_v1",
  "telecom_v2",
  "telecom_ai_v1",
  "telecom_self_v1",
]);

/**
 * @deprecated Use EVIDENCE_REQUIRED_QUESTIONNAIRES — kept as alias for backward compatibility.
 */
const TELECOM_STYLE_QUESTIONNAIRES = EVIDENCE_REQUIRED_QUESTIONNAIRES;

/**
 * Questionnaires that require all questions answered before submission.
 * Includes all evidence-required + HRV questionnaires.
 */
const ALL_QUESTIONS_REQUIRED_QUESTIONNAIRES = new Set([
  ...EVIDENCE_REQUIRED_QUESTIONNAIRES,
  "hrv_self_v1",
  "ai_llm_self_v1",
  "cyber_posture_v1",
  "data_privacy_law_v1",
  "iam_compromise_v1",
  "ir_ransomware_v1",
  "vendor_risk_lite_v1",
  "cyber_insurance_v1",
  "telecom_pre_investment_triage_v1",
]);

/** Returns true when the questionnaire is an HRV risk module. */
export function isHrvQuestionnaire(questionnaireId: string | null | undefined): boolean {
  return questionnaireId === "hrv_self_v1";
}

/** Returns true when the questionnaire has no reviewer workflow. */
export function isSelfAssessmentOnly(questionnaireId: string | null | undefined): boolean {
  if (!questionnaireId) return false;
  return SELF_ASSESSMENT_QUESTIONNAIRES.has(questionnaireId);
}

/**
 * Returns true when the questionnaire enforces evidence-required validation:
 * score >= 50 requires evidence OR comment OR link for ALL questions.
 */
export function isTelecomStyleQuestionnaire(questionnaireId: string | null | undefined): boolean {
  if (!questionnaireId) return false;
  return EVIDENCE_REQUIRED_QUESTIONNAIRES.has(questionnaireId);
}

/**
 * Returns true when the questionnaire requires ALL questions to be answered before submission.
 */
export function requiresAllQuestionsAnswered(questionnaireId: string | null | undefined): boolean {
  if (!questionnaireId) return false;
  return ALL_QUESTIONS_REQUIRED_QUESTIONNAIRES.has(questionnaireId);
}
